/**
 * ComposioConnectCoachHost — mounts the "the window will say Composio" coach
 * over ANY page (2026-08-20; same pattern as DesktopSignInModalHost). The
 * shared connect-popup engine dispatches `prest0n:connect-coach` CustomEvents
 * around the sign-in window's lifecycle, so a connect started from the chat
 * card gets the same coach the Integrations page always had — and every
 * surface's coach now hides the moment the sign-in window closes or the
 * connection resolves, instead of lingering.
 *
 * The flowId token ties each hide to the show it belongs to: an abandoned
 * earlier flow can never hide a newer flow's coach.
 *
 * Onboarding keeps its own locally-mounted coach (it drives launchConnectPopup
 * + its own poll, not runConnectPopupFlow, so no engine events fire there).
 */

import React, { useEffect, useState } from 'react';
import ComposioConnectCoach from './onboarding/ComposioConnectCoach';
import {
  COACH_EVENT,
  type ConnectCoachEventDetail,
} from '../services/composio/connectPopupFlow';

interface CoachState {
  flowId: number;
  appLabel?: string;
  /** Email journey (2026-08-20): the inbox the sign-in is pinned to. */
  intendedAddress?: string;
  toolkitSlug?: string;
}

export const ComposioConnectCoachHost: React.FC = () => {
  const [coach, setCoach] = useState<CoachState | null>(null);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail as ConnectCoachEventDetail | undefined;
      if (!detail || typeof detail.flowId !== 'number') return;
      if (detail.phase === 'show') {
        setCoach({
          flowId: detail.flowId,
          appLabel: detail.appLabel
            ? String(detail.appLabel).slice(0, 80)
            : detail.toolkitSlug
              ? String(detail.toolkitSlug).slice(0, 80)
              : undefined,
          ...(detail.intendedAddress
            ? { intendedAddress: String(detail.intendedAddress).slice(0, 120) }
            : {}),
          ...(detail.toolkitSlug
            ? { toolkitSlug: String(detail.toolkitSlug).toLowerCase().slice(0, 60) }
            : {}),
        });
        return;
      }
      // hide: only the show it belongs to.
      setCoach((current) => (current && current.flowId === detail.flowId ? null : current));
    };
    window.addEventListener(COACH_EVENT, onEvent as EventListener);
    return () => window.removeEventListener(COACH_EVENT, onEvent as EventListener);
  }, []);

  if (!coach) return null;
  return (
    <ComposioConnectCoach
      appLabel={coach.appLabel}
      intendedAddress={coach.intendedAddress}
      toolkitSlug={coach.toolkitSlug}
      onDismiss={() => setCoach(null)}
    />
  );
};

export default ComposioConnectCoachHost;
